import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { CenterPanel } from "@/components/layout/center-panel";
import { Button } from "@/components/ui/button";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { agentTasksApi, type AgentTask } from "@/api/agent-tasks";
import { queryKeys } from "@/lib/query-keys";
import { TaskDetailDrawer } from "@/components/tasks/task-detail-drawer";
import { NewTaskForm } from "@/components/tasks/new-task-form";
import { TaskList } from "@/components/tasks/task-list";
import { TaskTypeFilter } from "@/components/agent/agent-tasks-tab";

type Filter = "all" | "recurring" | "one_off";

interface TasksProps {
  /** When set, the agent dropdown is hidden and the list is locked to this agent. */
  lockedAgentId?: string;
  /** Skip the CenterPanel wrapper — for embedding inside another layout (e.g. workspace tab). */
  embedded?: boolean;
}

/** All agents' tasks: the same list as the other Tasks views, with the agent named. */
export function Tasks({ lockedAgentId, embedded = false }: TasksProps = {}) {
  const { tenantId, canManageAnyAgent } = useTenant();
  const [filter, setFilter] = useState<Filter>("all");
  const [agentFilter, setAgentFilter] = useState<string>("");
  const [selectedTask, setSelectedTask] = useState<AgentTask | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const effectiveAgentId = lockedAgentId ?? agentFilter;

  const { data: tasks = [], isLoading } = useQuery({
    // Include the filter in the queryKey so React Query re-fetches when the
    // dropdown changes.
    queryKey: [...queryKeys.agentTasks.byCompany(tenantId), { agentId: effectiveAgentId || null }],
    queryFn: () =>
      agentTasksApi.list(tenantId, effectiveAgentId ? { agentId: effectiveAgentId } : undefined),
    enabled: !!tenantId && (!lockedAgentId || !!effectiveAgentId),
  });

  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const filtered = filter === "all" ? tasks : tasks.filter((t) => t.type === filter);

  const body = (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <TaskTypeFilter value={filter} onChange={setFilter} tasks={tasks} />
        {!lockedAgentId && (
          <select
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs"
          >
            <option value="">All agents</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        )}
        {canManageAnyAgent && (
          <Button size="sm" className="ml-auto" onClick={() => setShowCreate(!showCreate)}>
            <Plus size={14} className="mr-1" />
            New Task
          </Button>
        )}
      </div>

      {showCreate && (
        <NewTaskForm agentId={lockedAgentId || agentFilter || undefined} onDone={() => setShowCreate(false)} />
      )}

      {isLoading ? (
        <p className="py-8 text-center text-sm text-text-muted">Loading tasks...</p>
      ) : (
        <TaskList
          tasks={filtered}
          showAgent={!effectiveAgentId}
          onOpen={setSelectedTask}
          emptyText="No tasks found."
        />
      )}

      <TaskDetailDrawer task={selectedTask} tenantId={tenantId} onClose={() => setSelectedTask(null)} />
    </div>
  );

  if (embedded) {
    return <div className="h-full overflow-y-auto px-6 py-4">{body}</div>;
  }
  return <CenterPanel title="Tasks">{body}</CenterPanel>;
}
